import { useEffect, useState } from "react";
import { Controller, FieldError, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { array, date, number, object, ref, string } from "yup";

import AutoComplete from "components/AutoComplete";
import Button from "components/Button";
import DatePicker from "components/DatePicker";
import GoogleAutoComplete from "components/GoogleAutoComplete";
import Header from "components/Header";
import Input from "components/Input";
import { routes } from "constants/routes";
import { EQUIPMENT_TYPES } from "constants/signup";
import { REGION_DATA } from "constants/trucks";
import { parseError } from "helpers/convertFormData";
import { showErrorMessage, showSuccessMessage } from "helpers/toastMessage";
import useCurrentLocation from "hooks/useCurrentLocation";
import MainLayout from "layouts/Main";
import { AddressInfo } from "models/User";
import { AppDispatch } from "store";
import { handleLoading } from "store/global/loader";
import { IOption } from "../../../../models/Form";
import { QUERY_TRUCK_DETAIL } from "../Detail/query";
import TruckPostSubHeader from "../modules/SubHeaders/Post";
import { MUTATION_CREATE_TRUCK, MUTATION_UPDATE_TRUCK } from "./query";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

export const postTruckByCityFormSchema = object({
  origin: object({
    city: string(),
    state: string()
  })
    .required("Must include both city and state")
    .test("both-required", "Must include both city and state", function (value) {
      if (!value) return false;
      const { city, state } = value;
      return Boolean(city && state);
    }),
  destination: object({
    city: string(),
    state: string()
  })
    .required("Must include both city and state")
    .test("both-required", "Must include both city and state", function (value) {
      if (!value) return false;
      const { city, state } = value;
      return Boolean(city && state);
    }),
  originRadius: number()
    .nullable()
    .transform((val, orig) => (orig === "" ? undefined : val))
    .integer("Origin radius must be an integer")
    .min(5, "Origin radius must be greater than or equal to 5")
    .max(500, "Origin radius must be less than or equal to 500"),
  destinationRadius: number()
    .nullable()
    .transform((val, orig) => (orig === "" ? undefined : val))
    .integer("Destination radius must be an integer")
    .min(5, "Destination radius must be greater than or equal to 5")
    .max(500, "Destination radius must be less than or equal to 500"),
  trailerTypes: array().min(1, "Please select trailer types").required("This field is required"),
  weight: number()
    .nullable()
    .transform((val, orig) => (orig === "" ? undefined : val))
    .integer("Weight must be an integer")
    .min(1000, "Weight must be greater than or equal to 1000 (lbs) pounds."),
  length: number()
    .nullable()
    .transform((val, orig) => (orig === "" ? undefined : val))
    .integer("Length must be an integer")
    .max(100, "Length must be less than or equal to 100 ft"),
  timeStart: date()
    .required("This field is required")
    .min(dayjs().subtract(1, "day"), "Must be no earlier than " + dayjs().format("MM/DD/YYYY")),
  timeEnd: date()
    .required("This field is required")
    .min(ref("timeStart"), "Must be no earlier than start date.")
});

export const postTruckFormByReStateSchema = object<Partial<ITruckPostData>>({
  trailerTypes: array().min(1, "Please select trailer types").required("This field is required"),
  weight: number()
    .nullable()
    .transform((val, orig) => (orig === "" ? undefined : val))
    .integer("Weight must be an integer")
    .min(1000, "Weight must be greater than or equal to 1000 (lbs) pounds."),
  timeStart: date()
    .required("This field is required")
    .min(dayjs().subtract(1, "day"), "Date is Too early."),
  timeEnd: date()
    .required("This field is required")
    .min(ref("timeStart"), "Must be no earlier than start Date")
});

interface ITruckPostData {
  origin: AddressInfo;
  originRegion: IOption[];
  originRadius?: number;
  destination: AddressInfo;
  destinationRegion: IOption[];
  destinationRadius: number;
  trailerTypes: IOption[];
  weight: number;
  length: number;
  targetRate: number;
  timeStart: string;
  timeEnd: string;
}

export default function TruckPost() {
  const { postedId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { refreshLocation } = useCurrentLocation();
  const [postType, setPostType] = useState("city");
  const [targetRateValue, setTargetRateValue] = useState<number>();
  const { trackAllEvents } = useAnalytics();

  const {
    control,
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    reset: resetForm,
    watch
  } = useForm<Partial<ITruckPostData>>({
    mode: "all",
    resolver: yupResolver(
      postType === "city" ? postTruckByCityFormSchema : postTruckFormByReStateSchema
    )
  });

  const origin = watch("origin");
  const destination = watch("destination");

  const { data, loading, error } = useQuery(QUERY_TRUCK_DETAIL, {
    variables: { id: postedId },
    skip: !postedId,
    fetchPolicy: "cache-and-network"
  });

  const [CreateTruck] = useMutation(MUTATION_CREATE_TRUCK, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  const [UpdateTruck] = useMutation(MUTATION_UPDATE_TRUCK, {
    onError(err) {
      err.graphQLErrors.map(({ message }) => showErrorMessage(parseError(message)));
    }
  });

  useEffect(() => {
    dispatch(handleLoading(loading));

    if (error) showErrorMessage(error.message);
  }, [loading, error]);

  useEffect(() => {
    if (data?.truck) {
      const selectedTruck = data.truck;
      const originArr = selectedTruck.originRegion.map(
        ({ originStates }: { originStates: string }) => originStates ?? ""
      );
      const destinationArr = selectedTruck.destinationRegion.map(
        ({ destinationStates }: { destinationStates: string }) => destinationStates ?? ""
      );
      const trailerTypeArr = selectedTruck.trailerTypes.map(
        ({ loadtypes }: { loadtypes: string }) => loadtypes
      );

      const origin = REGION_DATA.filter((item) => originArr.includes(item.value));
      const destination = REGION_DATA.filter((item) => destinationArr.includes(item.value));

      resetForm({
        origin: {
          city: selectedTruck.originCity ?? "",
          state: REGION_DATA.find((item) => originArr.includes(item.value))?.value
        },
        originRegion: origin,
        originRadius: selectedTruck.originRadius,
        destination: {
          city: selectedTruck.destinationCity ?? "",
          state: REGION_DATA.find((item) => destinationArr.includes(item.value))?.value
        },
        destinationRegion: destination,
        destinationRadius: selectedTruck.destinationRadius,
        trailerTypes: EQUIPMENT_TYPES.filter(({ value }) => trailerTypeArr?.includes(value)),
        weight: selectedTruck.weight,
        length: selectedTruck.length,
        targetRate: selectedTruck.targetRate,
        timeStart: selectedTruck.timeStart
          ? dayjs(selectedTruck.timeStart).format("MM/DD/YYYY")
          : "",
        timeEnd: selectedTruck.timeEnd ? dayjs(selectedTruck.timeEnd).format("MM/DD/YYYY") : ""
      });
    } else {
      resetForm({
        weight: 45000,
        length: 53,
        timeStart: dayjs().format("MM/DD/YYYY"),
        timeEnd: dayjs().add(1, "day").format("MM/DD/YYYY")
      });
    }
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (Number(newValue) < 10 && newValue.length < 5) {
      setTargetRateValue(newValue as any);
    }
  };

  const goPostedTrucks = () => navigate(routes.TRUCKS_MY_POSTED);

  const getCurrentState = async () => {
    try {
      const { currentState } = await refreshLocation();
      if (currentState) {
        const state = REGION_DATA.find((region) => region.label === currentState);
        state && setValue("originRegion", [state]);
      }
    } catch {
      showErrorMessage("Error getting location");
    }
  };

  const getCurrentLocation = async () => {
    try {
      const { addressObject } = await refreshLocation();
      addressObject && setValue("origin", addressObject);
    } catch {
      showErrorMessage("Error getting location");
    }
  };

  const onSubmit = async (postTruckData: ITruckPostData) => {
    const {
      origin,
      originRegion,
      originRadius,
      destination,
      destinationRegion,
      destinationRadius,
      trailerTypes,
      weight,
      length,
      targetRate,
      timeStart,
      timeEnd
    } = postTruckData;

    const originRegionStrArr =
      postType === "city" ? [origin?.state] : originRegion.map((item) => item.value);
    const destinationRegionStrArr =
      postType === "city" ? [destination?.state] : destinationRegion.map((item) => item.value);
    const trailerTypesStrArr = trailerTypes.map((item) => item.value);

    const submitData = {
      ...(postType === "city"
        ? {
            originCity: origin?.city ?? "",
            originCountry: origin?.country ?? "",
            destinationCity: destination?.city,
            destinationCountry: destination?.country
          }
        : {
            originCity: "",
            originCountry: "",
            destinationCity: "",
            destinationCountry: ""
          }),
      originRadius: Number(originRadius) !== 0 ? Number(originRadius) : 200,
      originRegion: originRegionStrArr,
      destinationRadius: Number(destinationRadius) !== 0 ? Number(destinationRadius) : 200,
      destinationRegion: destinationRegionStrArr,
      trailerTypes: trailerTypesStrArr,
      weight: Number(weight),
      length: Number(length),
      ...(targetRate && { targetRate: Number(targetRate) }),
      timeStart: dayjs(timeStart).format("YYYY-MM-DDTHH:mm:ssZ"),
      timeEnd: dayjs(timeEnd).format("YYYY-MM-DDTHH:mm:ssZ")
    };

    try {
      dispatch(handleLoading(true));

      if (postedId) {
        await UpdateTruck({
          variables: { id: postedId, ...submitData }
        })
          .then(() => {
            showSuccessMessage("Successfully updated truck.");
          })
          .catch(() => {
            showErrorMessage("Update truck failed.");
          });
      } else {
        await CreateTruck({
          variables: { ...submitData }
        })
          .then(() => {
            showSuccessMessage("Successfully posted truck.");
          })
          .catch(() => {
            showErrorMessage("Create truck failed.");
          });
      }
      goPostedTrucks();
      trackAllEvents("truck_post_post_truck");
    } finally {
      dispatch(handleLoading(false));
    }
  };

  return (
    <MainLayout
      desktopTitle="Post Truck"
      customHeader={<Header label="Post Truck" goBackPath={routes.TRUCKS} isHideDesktop />}
      className="pl-3.5 pr-3.5 flex-1 overflow-auto">
      <form className={styles.truckPostBody} onSubmit={handleSubmit(onSubmit)}>
        <TruckPostSubHeader postType={postType} setPostType={setPostType} />

        <div className={styles.truckPostWrapper}>
          <div className={styles.inputFields}>
            <div className={styles.truckPostCard}>
              <div className={styles.origin}>
                {postType === "city" ? (
                  <div className={styles.fields}>
                    <Controller
                      name="origin"
                      control={control}
                      render={({ field: { onChange, onBlur } }) => {
                        return (
                          <GoogleAutoComplete
                            placeholder="Origin"
                            value={origin}
                            onChange={onChange}
                            onBlur={onBlur}
                            error={
                              (errors.origin?.state as FieldError) ||
                              (errors.origin?.city as FieldError) ||
                              (errors.origin as FieldError)
                            }
                            label={
                              <div className="pl-2">
                                <span className="mr-2">Origin</span>
                                <button
                                  type="button"
                                  className="text-primary"
                                  onClick={getCurrentLocation}>
                                  Current Location
                                </button>
                              </div>
                            }
                            containerClass="flex-1"
                          />
                        );
                      }}
                    />
                    <Input
                      type="number"
                      inputMode="numeric"
                      name="originRadius"
                      label="&nbsp;&nbsp;Radius"
                      register={register}
                      error={errors.originRadius as FieldError}
                      placeholder="200"
                      className="w-1/3"
                    />
                  </div>
                ) : (
                  <AutoComplete
                    name="originRegion"
                    label={
                      <div className="pl-2">
                        <span className="mr-2">Origin</span>
                        <button type="button" className="text-primary" onClick={getCurrentState}>
                          Current State
                        </button>
                      </div>
                    }
                    placeholder="Any"
                    options={REGION_DATA}
                    control={control}
                    className="flex-1 mb-2"
                    isMulti
                  />
                )}
              </div>

              <div className={styles.destination}>
                {postType === "city" ? (
                  <div className={styles.fields}>
                    <Controller
                      name="destination"
                      control={control}
                      render={({ field: { onChange, onBlur } }) => {
                        return (
                          <GoogleAutoComplete
                            placeholder="Destination"
                            value={destination}
                            onChange={onChange}
                            onBlur={onBlur}
                            error={
                              (errors.destination?.state as FieldError) ||
                              (errors.destination?.city as FieldError) ||
                              (errors.destination as FieldError)
                            }
                            label="&nbsp;&nbsp;Destination"
                            containerClass="flex-1"
                          />
                        );
                      }}
                    />
                    <Input
                      type="number"
                      inputMode="numeric"
                      name="destinationRadius"
                      label="&nbsp;&nbsp;Radius"
                      register={register}
                      error={errors.destinationRadius as FieldError}
                      placeholder="200"
                      className="w-1/3"
                    />
                  </div>
                ) : (
                  <AutoComplete
                    name="destinationRegion"
                    label="&nbsp;&nbsp;Destination"
                    placeholder="Any"
                    options={REGION_DATA}
                    control={control}
                    className="flex-1"
                    isMulti
                  />
                )}
              </div>
            </div>

            <AutoComplete
              name="trailerTypes"
              label="&nbsp;&nbsp;Equipment Type"
              placeholder="Any"
              options={EQUIPMENT_TYPES}
              control={control}
              error={errors.trailerTypes as FieldError}
              className="flex-1"
              isMulti
              innerClassName="equipment-type-dropdown"
            />

            <div className={styles.truckPostWeight}>
              <Input
                type="number"
                inputMode="numeric"
                name="weight"
                register={register}
                error={errors.weight}
                label="Weight (Lbs)"
                className="flex-1"
                placeholder="45000"
              />
              <Input
                type="number"
                inputMode="numeric"
                name="length"
                register={register}
                error={errors.length}
                label="Length (ft.)"
                className="flex-1"
                placeholder="53"
              />
              <Input
                type="text"
                inputMode="numeric"
                name="targetRate"
                register={register}
                error={errors.targetRate}
                label="Rate Per Mile"
                className="flex-1"
                value={targetRateValue}
                isSingleNumber
                onInput={handleChange}
              />
            </div>

            <Controller
              name="timeStart"
              control={control}
              render={({ field: { value = "", onChange } }) => (
                <DatePicker
                  value={value}
                  onChange={onChange}
                  error={errors.timeStart as FieldError}
                  label="&nbsp;&nbsp;Ready Start"
                  placeholder="Select Date"
                  className="mt-3"
                />
              )}
            />
            <Controller
              name="timeEnd"
              control={control}
              render={({ field: { value = "", onChange } }) => (
                <DatePicker
                  value={value}
                  onChange={onChange}
                  error={errors.timeEnd as FieldError}
                  label="&nbsp;&nbsp;Ready End"
                  placeholder="Select Date"
                  className="mt-3"
                />
              )}
            />
          </div>

          <div className={styles.truckPostFooter}>
            <Button type="submit" size="lg" className={styles.truckPostFooterButton}>
              {postedId ? "Repost Truck" : "Post Truck"}
            </Button>
          </div>
        </div>
      </form>
    </MainLayout>
  );
}
